import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Trenger restauranten din online menyadministrasjon?",
  },
  {
    id: "definition",
    label: "Hva betyr det å administrere en meny online?",
  },
  {
    id: "costs",
    label: "Hvorfor raske prisoppdateringer er viktige for restauranter",
  },
  {
    id: "availability",
    label: "Skjul retter når de ikke er tilgjengelige",
  },
  {
    id: "daily-menu",
    label: "Publiser dagens meny og sesongtilbud raskere",
  },
  {
    id: "photos",
    label: "Forbedre bilder og beskrivelser direkte",
  },
  {
    id: "pdf",
    label: "Hvorfor PDF-menyer er vanskelige når endringer skjer ofte",
  },
  {
    id: "workflow",
    label: "Hvordan en god arbeidsflyt for menyadministrasjon ser ut",
  },
  {
    id: "mistakes",
    label: "Vanlige feil i online menyadministrasjon",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å administrere menyer online",
  },
  {
    id: "faq",
    label: "FAQ om online menyadministrasjon",
  },
];

const checklist = [
  "Du endrer priser eller dagens tilbud regelmessig.",
  "Noen retter er midlertidig utsolgt.",
  "Du vil skjule produkter raskt og vise dem igjen senere.",
  "Du vil redigere bilder, beskrivelser eller kategorier selv.",
  "Du vil ikke lage en ny PDF for hver eneste endring.",
  "Du vil være mindre avhengig av trykkeri, designere eller utviklere.",
  "Gjester sjekker allerede menyen din online før de besøker restauranten.",
  "Du vil publisere sesongtilbud raskere.",
  "Teamet ditt bør jobbe med oppdatert menyinformasjon.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Online menyadministrasjon er kanskje ikke akutt ennå",
      text:
        "Hvis menyen din sjelden endres, kan en enkel digital versjon være nok foreløpig. Likevel kan enklere oppdateringer bli nyttige over tid.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Online menyadministrasjon er nyttig",
      text:
        "Restauranten din kan ha nytte av å endre priser, tilgjengelighet, bilder og dagens tilbud raskere uten å lage nye filer eller trykke på nytt.",
      tone: "medium",
    };
  }

  return {
    title: "Online menyadministrasjon kan hjelpe mye i daglig drift",
    text:
      "Flere punkter viser at restauranten din trenger en redigerbar online meny: oppdaterte priser, skjulte utsolgte retter, dagens tilbud, bilder og mindre arbeid med endringer.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function ManageMenuOnlineForRestaurantsNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Administrer meny online for restauranter: priser, tilbud og tilgjengelighet | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/speisekarte-online-verwalten",
          en: "/en/manage-menu-online-for-restaurants",
          no: "/no/administrere-meny-online-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Administrer restaurantmenyen online: oppdater priser, dagens
              tilbud og tilgjengelighet raskere
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En redigerbar online meny hjelper restauranter med å reagere
                raskere: oppdatere priser, skjule utsolgte retter, publisere
                dagens tilbud og forbedre bilder eller beskrivelser uten å lage
                en ny PDF.
              </p>

              <p className="seoHero__text">
                Fordelen handler ikke bare om design. Den virkelige fordelen
                vises i det daglige arbeidet: når noe endres, bør restauranten
                ikke alltid trenge en ny fil, nytt trykk eller en utvikler.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Trenger restauranten din online menyadministrasjon?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: ikke alle restauranter trenger avansert
              menyadministrasjon fra dag én, men mange restauranter kan ha
              nytte av raskere oppdateringer mye tidligere enn de tror.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Hva betyr det å administrere en meny online?</h2>

          <p>
            Å administrere en meny online betyr at en restaurant kan redigere
            menyinnhold gjennom et digitalt grensesnitt: kategorier, retter,
            priser, beskrivelser, bilder og tilgjengelighet.
          </p>

          <p>
            I stedet for å lage en ny fil eller spørre noen om hver lille
            endring, kan eieren eller administratoren oppdatere menyen direkte.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Endre priser.</li>
            <li className="text__seo-li">Legge til retter.</li>
            <li className="text__seo-li">Skjule produkter.</li>
            <li className="text__seo-li">Oppdatere bilder.</li>
            <li className="text__seo-li">Forbedre beskrivelser.</li>
            <li className="text__seo-li">Endre rekkefølge på kategorier.</li>
            <li className="text__seo-li">Publisere dagens tilbud.</li>
            <li className="text__seo-li">Administrere sesongretter.</li>
          </ul>

          <h3>Menyen bør kunne redigeres i daglig arbeid</h3>

          <p>
            En digital meny er mest nyttig når den kan endres av personene som
            faktisk jobber med menyen: eieren, lederen eller en ansvarlig
            medarbeider.
          </p>
        </section>

        <section id="costs" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor raske prisoppdateringer er viktige for restauranter</h2>

            <p>
              Restauranter jobber med endrede innkjøpspriser, personalkostnader,
              energikostnader og sesongprodukter. Når kostnader endres, må noen
              ganger menypriser, tilbudsstruktur eller porsjoner justeres.
            </p>

            <p>
              En redigerbar online meny fjerner ikke kostnadspresset. Men den
              hjelper restauranten med å holde synlig informasjon oppdatert uten
              å starte en full trykk- eller filprosess hver gang.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Treg oppdateringsprosess</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Prisendringen noteres.</li>
                  <li className="text__seo-li">
                    PDF eller trykkfil må redigeres.
                  </li>
                  <li className="text__seo-li">
                    Noen må laste den opp igjen.
                  </li>
                  <li className="text__seo-li">
                    Gamle versjoner kan fortsatt ligge online.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre prosess</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Åpne menyadministrasjonen.</li>
                  <li className="text__seo-li">Endre prisen.</li>
                  <li className="text__seo-li">Lagre oppdateringen.</li>
                  <li className="text__seo-li">
                    QR-menyen viser den aktuelle versjonen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="availability" className="acquaintance">
          <h2>Skjul retter når de ikke er tilgjengelige</h2>

          <p>
            I restaurantdrift er ikke alle produkter tilgjengelige hele tiden.
            Ingredienser kan mangle, en rett kan være utsolgt, eller et tilbud
            kan gjelde bare på bestemte tidspunkter.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Statisk meny</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Utsolgte retter blir fortsatt synlige.
                </li>
                <li className="text__seo-li">
                  Gjester bestiller noe som ikke er tilgjengelig.
                </li>
                <li className="text__seo-li">
                  Personalet må forklare og skuffe gjesten.
                </li>
                <li className="text__seo-li">Endringer går sakte.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Redigerbar online meny</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Retter kan skjules midlertidig.
                </li>
                <li className="text__seo-li">
                  Tilgjengeligheten holder seg mer aktuell.
                </li>
                <li className="text__seo-li">Færre misforståelser.</li>
                <li className="text__seo-li">
                  Produkter kan aktiveres igjen senere.
                </li>
              </ul>
            </div>
          </div>

          <p>
            Målet er ikke å erstatte service. Målet er å redusere unødvendig
            skuffelse før den oppstår.
          </p>
        </section>

        <section id="daily-menu" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Publiser dagens meny og sesongtilbud raskere</h2>

            <p>
              Dagens retter, lunsjtilbud, sesongretter eller kortvarige
              kampanjer fungerer best når gjestene ser dem på riktig tidspunkt.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">Opprett dagens rett.</li>
              <li className="text__seo-li">Legg til pris.</li>
              <li className="text__seo-li">Legg til bilde.</li>
              <li className="text__seo-li">Gjør den synlig online.</li>
              <li className="text__seo-li">Skjul den senere.</li>
            </ol>

            <p>
              Dette kan være spesielt nyttig for kafeer, bistroer, pizzeriaer
              og restauranter med skiftende tilbud. Menyen vises ikke bare
              digitalt — den administreres aktivt.
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/en/create-digital-daily-menu">
                    Lag en digital dagens meny
                  </Link>
                </p>
              </div>

              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/endre-meny-online">
                    Endre meny online
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="photos" className="acquaintance">
          <h2>Forbedre bilder og beskrivelser direkte</h2>

          <p>
            Gode bilder og tydelige beskrivelser hjelper gjester med å forstå en
            rett raskere. Dette er spesielt viktig for nye retter,
            sesongtilbud, menyer eller produkter som trenger forklaring.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Bytte bilde.</li>
            <li className="text__seo-li">Korte ned beskrivelsen.</li>
            <li className="text__seo-li">Forklare ingredienser.</li>
            <li className="text__seo-li">Beskrive styrkegrad.</li>
            <li className="text__seo-li">Vise tilbehør.</li>
            <li className="text__seo-li">Fremheve anbefalinger.</li>
          </ul>

          <p>
            En digital meny bør ikke være «ferdig» etter første opplasting. Den
            bør kunne forbedres etter hvert som restauranten lærer hva gjestene
            trenger.
          </p>

          <VisualIdea>
            Et enkelt administrasjonspanel der eieren bytter bilde av en rett,
            forkorter beskrivelsen og sjekker den oppdaterte menyen på en
            smarttelefon.
          </VisualIdea>
        </section>

        <section id="pdf" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor PDF-menyer er vanskelige når endringer skjer ofte</h2>

            <p>
              En PDF kan fungere i starten. Men når priser, dagens tilbud eller
              tilgjengelighet endres regelmessig, blir en PDF upraktisk.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>PDF-meny</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Hver endring skaper en ny fil.
                  </li>
                  <li className="text__seo-li">
                    Gamle versjoner kan fortsette å sirkulere.
                  </li>
                  <li className="text__seo-li">
                    Ofte ukomfortabel på smarttelefon.
                  </li>
                  <li className="text__seo-li">
                    Ingen enkel synlighetslogikk for utsolgte produkter.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Online meny</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Endringer gjøres i administrasjonsområdet.
                  </li>
                  <li className="text__seo-li">
                    Den aktuelle versjonen åpnes via QR-kode.
                  </li>
                  <li className="text__seo-li">Bedre mobilopplevelse.</li>
                  <li className="text__seo-li">
                    Produkter kan vises eller skjules.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Hvis gjester ser menyen online før de besøker restauranten, bør
              den synlige menyen være så oppdatert som mulig.
            </p>
          </div>
        </section>

        <section id="workflow" className="acquaintance">
          <h2>Hvordan en god arbeidsflyt for menyadministrasjon ser ut</h2>

          <p>
            God menyadministrasjon bør være enkel nok til at den faktisk brukes
            i restaurantens hverdag.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              Administratoren åpner menyadministrasjonen.
            </li>
            <li className="text__seo-li">
              Kategori eller produkt velges.
            </li>
            <li className="text__seo-li">
              Pris, bilde eller beskrivelse endres.
            </li>
            <li className="text__seo-li">
              Produktet gjøres synlig, skjules eller oppdateres.
            </li>
            <li className="text__seo-li">Endringen lagres.</li>
            <li className="text__seo-li">
              Gjesten ser den oppdaterte versjonen via QR-kode eller lenke.
            </li>
          </ol>

          <p>
            Arbeidsflyten trenger ikke å være komplisert. Det viktigste er at
            endringer ikke blir forsinket fordi prosessen er for tung.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige feil i online menyadministrasjon</h2>

            <p>
              Online menyadministrasjon hjelper bare når prosessen er tydelig
              for restaurantteamet. Hvis redigering er forvirrende, blir menyen
              likevel utdatert.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    For mange obligatoriske felt for små endringer.
                  </li>
                  <li className="text__seo-li">
                    Brukere forstår ikke om et produkt er offentlig eller skjult.
                  </li>
                  <li className="text__seo-li">
                    Gamle priser blir liggende online.
                  </li>
                  <li className="text__seo-li">
                    Dårlige eller utdaterte bilder forblir synlige.
                  </li>
                  <li className="text__seo-li">
                    Dagens tilbud vises for lenge.
                  </li>
                  <li className="text__seo-li">
                    Den offentlige mobilvisningen sjekkes ikke etter
                    oppdateringer.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Hold redigering enkel og rask.
                  </li>
                  <li className="text__seo-li">Gjør synlighet tydelig.</li>
                  <li className="text__seo-li">
                    Oppdater priser når de endres.
                  </li>
                  <li className="text__seo-li">Bytt svake bilder.</li>
                  <li className="text__seo-li">
                    Fjern eller skjul gamle dagens tilbud.
                  </li>
                  <li className="text__seo-li">
                    Sjekk menyen slik gjestene ser den.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <SeoWorkSteps lang="no" />

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om online menyadministrasjon</h2>

          <div className="seoFaq">
            <details>
              <summary>Kan jeg endre priser selv?</summary>
              <p>
                Ja. Dette er en av de viktigste fordelene med en redigerbar
                online meny.
              </p>
            </details>

            <details>
              <summary>Kan jeg skjule utsolgte retter?</summary>
              <p>
                Ja. Et godt system for digital menyadministrasjon bør la deg
                skjule produkter midlertidig og aktivere dem igjen senere.
              </p>
            </details>

            <details>
              <summary>Er en PDF-meny nok?</summary>
              <p>
                For en enkel start kan en PDF være nok. Hvis endringer skjer
                ofte, er en redigerbar online meny vanligvis mer praktisk.
              </p>
            </details>

            <details>
              <summary>Trenger jeg en utvikler for endringer?</summary>
              <p>
                Målet med god menyadministrasjon er at eiere eller
                administratorer kan gjøre viktige endringer selv.
              </p>
            </details>

            <details>
              <summary>Kan jeg endre dagens tilbud online?</summary>
              <p>
                Ja. Dagens retter, sesongtilbud eller kampanjer er typiske
                bruksområder for online menyadministrasjon.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Flere temaer om digitale menyer</h2>

          <div className="seoCompareSmall">
            <p>
              <Link to="/no/digital-meny-for-restauranter">
                Digital meny for restauranter
              </Link>
            </p>

            <p>
              <Link to="/no/test-gratis-digital-meny">
                Test en gratis digital meny
              </Link>
            </p>

            <p>
              <Link to="/no/qr-kode-meny-uten-app">
                QR-kode-meny uten app
              </Link>
            </p>

            <p>
              <Link to="/no/endre-meny-online">
                Endre meny online
              </Link>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
}